import { AnalyticsBrowser } from '@segment/analytics-next'
import Browser from 'bowser'
import { isPwaInstalled } from '@/common/pwa/installPromptAdapter'
import useAccountStore from '@/account/accountStore'
import { captureUTMParameters, getStoredUTMParameters } from './utm'
import { VITE_SEGMENT_WRITE_KEY, VITE_APP_VERSION, VITE_DOMAIN } from './Constants'

const DEFINITION_VIEW_DEBOUNCE_MS = 2000
const lastEventTimestamps = new Map<string, number>()

/**
 * Every tracked event is stamped with the currently active account context.
 * Individual event props can still pass an explicit `accountId` (e.g. onboarding flows
 * that fire before the new account is set active) - it simply overrides this default.
 */
const getDefaultAnalyticsData = () => {
    let deviceType = 'unknown'

    if (typeof window !== 'undefined' && window.navigator && window.navigator.userAgent) {
        try {
            deviceType = Browser.getParser(window.navigator.userAgent).getPlatformType()
        } catch {
            // ignore parsing errors and keep 'unknown'
        }
    }

    const activeAccount = useAccountStore.getState().getActiveAccount()

    return {
        version: VITE_APP_VERSION,
        deviceType,
        isPwa: isPwaInstalled(),
        accountId: activeAccount?.accountId ?? null,
        adviserGroupId: activeAccount?.adviserId ?? null,
    }
}

const instance = AnalyticsBrowser.load(
    {
        writeKey: VITE_SEGMENT_WRITE_KEY,
    },
    {
        cookie: {
            domain: VITE_DOMAIN,
        },
    },
)

const trackWithDefaults = (event: string, properties: Record<string, any> = {}) => {
    instance.track(event, { ...getDefaultAnalyticsData(), ...properties }).catch(() => {
        // ignore tracking errors
    })
}

export const analytics = {
    ...instance,
    // Overrides the raw Segment `track`/`page` so every call site gets default/account context
    // for free, instead of only call sites that remember to use `trackWithDefaults`.
    track: trackWithDefaults,
    trackWithDefaults,
    page: (properties: Record<string, any> = {}) => {
        // Explicit category/name (null) keeps the merged object unambiguously in the
        // `properties` slot of Segment's page(category, name, properties, options) signature.
        instance.page(null, null, { ...getDefaultAnalyticsData(), ...properties }).catch(() => {
            // ignore tracking errors
        })
    },
    trackWithUtm: (event: string, properties: Record<string, any> = {}) => {
        const utm = getStoredUTMParameters()
        instance.track(event, { ...getDefaultAnalyticsData(), ...utm, ...properties }).catch(() => {
            // ignore tracking errors
        })
    },
    trackWithDefaultsDebounced: (
        event: string,
        key: string,
        properties: Record<string, any>,
        debounceMs = DEFINITION_VIEW_DEBOUNCE_MS,
    ) => {
        const compositeKey = `${event}:${key}`
        const now = Date.now()
        const last = lastEventTimestamps.get(compositeKey) ?? 0
        if (now - last < debounceMs) return
        lastEventTimestamps.set(compositeKey, now)
        trackWithDefaults(event, properties)
    },
}

export const captureUtmOnFirstLoad = () => {
    const UTM_TRACKED_KEY = 'utm_tracked'
    captureUTMParameters()

    if (!sessionStorage.getItem(UTM_TRACKED_KEY)) {
        const utm = getStoredUTMParameters()
        if (utm) {
            analytics.track('UTM Captured', utm)
            sessionStorage.setItem(UTM_TRACKED_KEY, 'true')
        }
    }
}
